import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface CreateDataPayload {
    name: string;
    code: string;
    payment_bank_id: string;
    payment_gateway_id: string;
    payment_method_id: string;
    status: 'active' | 'inactive';
}

interface PaymentChannel {
    id: string;
    name: string;
    code: string;
    payment_bank_id: string;
    payment_gateway_id: string;
    payment_method_id: string;
    status: 'active' | 'inactive';
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

@Component({
  selector: 'app-payment-channel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './payment-channel.component.html',
  styleUrl: './payment-channel.component.scss'
})
export class PaymentChannelComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    selectedPaymentChannelId: string = '';

    paymentChannel: PaymentChannel[] = [];
    paymentGateway: any[] = [];
    paymentMethod: any[] = [];
    paymentBank: any[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_page: 1
    };

    createDataForm: CreateDataPayload = {
        name: '',
        code: '',
        payment_bank_id: '',
        payment_gateway_id: '',
        payment_method_id: '',
        status: 'inactive'
    };

    constructor(private apiService: ApiService, private router: Router) { }

    ngOnInit(): void {
        this.getPaymentChannel();
        this.getPaymentGateway();
        this.getPaymentMethod();
        this.getPaymentBank();
    }

    async getPaymentChannel(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/payment-channel/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log('Payment Channels:', response);
            this.paymentChannel = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            console.error('Error fetching payment channels:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch payment channels.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentGateway(): Promise<void> {
        try {
            const response = await this.apiService.get('/api/v1/tenant/manage/payment-gateway/list');
            // console.log('Payment Gateways:', response);
            this.paymentGateway = response.data.list;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Terjadi kesalahan saat mengambil data payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentMethod(): Promise<void> {
        try {
            const response = await this.apiService.get('/api/v1/tenant/manage/payment-method/list');
            // console.log('Payment Methods:', response);
            this.paymentMethod = response.data.list;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment method.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentBank(): Promise<void> {
        try {
            const response = await this.apiService.get('/api/v1/tenant/manage/payment-bank/list');
            // console.log('Payment Banks:', response);
            this.paymentBank = response.data.list;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment bank.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentChannelById(paymentChannelId: string): Promise<void> {
        try {
            const response = await this.apiService.get(`/api/v1/tenant/manage/payment-channel/${paymentChannelId}`);
            // console.log('Payment Channel Details:', response);
            this.createDataForm = {
                name: response.data.name,
                code: response.data.code,
                payment_bank_id: response.data.payment_bank_id,
                payment_gateway_id: response.data.payment_gateway_id,
                payment_method_id: response.data.payment_method_id,
                status: response.data.status
            }
            this.selectedPaymentChannelId = paymentChannelId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Terjadi kesalahan saat mengambil data payment channel.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async createData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/payment-channel', this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment channel berhasil ditambahkan.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentChannel();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat menambahkan payment channel.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    async updateData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(`/api/v1/tenant/manage/payment-channel/${this.selectedPaymentChannelId}`, this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment channel berhasil diperbarui.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentChannel();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memperbarui payment channel.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        } finally {
            this.isSubmitting = false;
        }
    }

    async deleteData(paymentChannelId: string, paymentChannelName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus payment channel ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });
        if (result.isConfirmed) {
            try {
                await this.apiService.delete(`/api/v1/tenant/manage/payment-channel`, paymentChannelId);
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Payment channel berhasil dihapus.',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false
                });
                this.getPaymentBank();
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.error?.message || 'Terjadi kesalahan saat menghapus payment channel.',
                    confirmButtonColor: '#dc3545',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        }
    }

    openaddModal(): void {
        this.isModalOpen = true;
        this.resetForm();
    }

    openeditModal(paymentChannelId: string): void {
        this.getPaymentChannelById(paymentChannelId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.createDataForm = {
            name: '',
            code: '',
            payment_bank_id: '',
            payment_gateway_id: '',
            payment_method_id: '',
            status: 'inactive'
        };
    }

    validateDataForm(): boolean {
        if (!this.createDataForm.name || !this.createDataForm.code || !this.createDataForm.status) {
            Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: 'Semua field harus diisi.',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }
        return true;
    }

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.total_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getPaymentChannel();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateData();
        } else {
            await this.createData();
        }
    }
}
